import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import { staticEquipmentInputSchema, updateEquipmentBodySchema } from './schemas';

// ============================================================
// Param Schemas
// ============================================================

/** `/projects/:projectId/equipment/:equipmentId` — the project owns, `equipmentId` addresses. */
const equipmentParamsSchema = projectIdParamsSchema.extend({
	equipmentId: requiredStringSchema.describe('Equipment identifier'),
});

// ============================================================
// Contract
// ============================================================

/**
 * Static equipment contract. `{ root: true }`, so every path is absolute.
 * The collection's project field is the ordinary `projectId`.
 */
export const staticEquipmentContract = defineContract(
	'staticEquipment',
	{
		// ── Collection ────────────────────────────────────────
		insertEquipment: route({
			method: 'POST',
			path: '/projects/:projectId/equipment',
			params: projectIdParamsSchema,
			// `projectId` is out of `staticEquipmentInputSchema`; the path carries it.
			body: staticEquipmentInputSchema,
			response: z.string(),
			operationId: 'insertEquipment',
			tags: ['Static Equipment'],
			summary: 'Insert equipment',
		}),
		getEquipmentByProject: route({
			method: 'GET',
			path: '/projects/:projectId/equipment',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getEquipmentByProject',
			tags: ['Static Equipment'],
			summary: 'Get equipment by project',
		}),
		deleteEquipmentsByIds: route({
			method: 'DELETE',
			path: '/projects/:projectId/equipment',
			params: projectIdParamsSchema,
			body: z.object({ ids: z.array(z.string()) }),
			response: z.string(),
			operationId: 'deleteEquipmentsByIds',
			tags: ['Static Equipment'],
			summary: 'Delete multiple equipment by IDs',
		}),

		// ── Sub-collection (static segment, before `/:equipmentId`) ─
		sumEquipmentByType: route({
			method: 'GET',
			path: '/projects/:projectId/equipment/summary',
			params: projectIdParamsSchema,
			response: z.any(),
			operationId: 'sumEquipmentByType',
			tags: ['Static Equipment'],
			summary: 'Sum equipment by type',
		}),

		// ── Items ─────────────────────────────────────────────
		getEquipmentByEquipmentId: route({
			method: 'GET',
			path: '/projects/:projectId/equipment/:equipmentId',
			params: equipmentParamsSchema,
			response: z.any(),
			operationId: 'getEquipmentByEquipmentId',
			tags: ['Static Equipment'],
			summary: 'Get equipment by equipment ID',
		}),
		updateEquipment: route({
			method: 'PUT',
			path: '/projects/:projectId/equipment/:equipmentId',
			params: equipmentParamsSchema,
			// This body used to be `z.record(z.string(), z.unknown())` — any key at
			// all — and the handler passed it straight through as `fieldsToUpdate`.
			// A member of project A could `PUT { "projectId": "project-B" }` and
			// RELOCATE the row: the scope filter constrained which row was matched,
			// never what was written. The schema is explicit now and `projectId` is
			// not in it.
			body: updateEquipmentBodySchema,
			response: z.string(),
			operationId: 'updateEquipment',
			tags: ['Static Equipment'],
			summary: 'Update equipment',
		}),
		deleteEquipment: route({
			method: 'DELETE',
			path: '/projects/:projectId/equipment/:equipmentId',
			params: equipmentParamsSchema,
			response: z.string(),
			operationId: 'deleteEquipment',
			tags: ['Static Equipment'],
			summary: 'Delete equipment by ID',
		}),
	},
	{ root: true }
);
